import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../config/stripe';
import { depositsAPI } from '../../services/stripeAPI';

interface DepositDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

function DepositForm({ onClose, onSuccess }: { onClose: () => void; onSuccess?: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  
  const [amount, setAmount] = useState<string>('25.00');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    const amountNum = parseFloat(amount);
    
    // Validate amount
    if (isNaN(amountNum) || amountNum < 1 || amountNum > 10000) {
      setError('Amount must be between $1 and $10,000');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create payment intent on backend
      const { clientSecret, paymentIntentId } = await depositsAPI.createDeposit(amountNum);

      // Step 2: Confirm payment with Stripe.js
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 2000);
      } else {
        setError(`Payment status: ${paymentIntent.status}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Add prepaid credits to your account using a credit or debit card.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment successful! Credits will be added to your account shortly.
          </Alert>
        )}

        <TextField
          fullWidth
          label="Amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          inputProps={{
            min: 1,
            max: 10000,
            step: 0.01,
          }}
          sx={{ mb: 3 }}
          disabled={loading || success}
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Card Details
          </Typography>
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 2,
              backgroundColor: loading || success ? '#f5f5f5' : '#fff',
            }}
          >
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </Box>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Test Mode:</strong> Use card 4242 4242 4242 4242, any future expiry, any CVC.
          </Typography>
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Fee: 2.9% + $0.30 (charged by Stripe)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total cost: ${(parseFloat(amount || '0') * 1.029 + 0.30).toFixed(2)}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Processing...' : `Deposit $${amount}`}
        </Button>
      </DialogActions>
    </form>
  );
}

export default function DepositDialog({ open, onClose, onSuccess }: DepositDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Credits</DialogTitle>
      <Elements stripe={stripePromise}>
        <DepositForm onClose={onClose} onSuccess={onSuccess} />
      </Elements>
    </Dialog>
  );
}
